import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { IconArrowLeft } from "@tabler/icons-react";
import { Button, Input, Label } from "@/components/ui";
import {
  authApi,
  isTwoFactorChallenge,
  type LoginSession,
  type TwoFactorChallenge,
} from "@/services/api";
import { useAuthStore } from "@/stores/auth.store";

interface LoginForm {
  email: string;
  password: string;
}

// Sócios e Parceiros data
const socios = [
  {
    name: "Patria Investimentos",
    logo: "/images/Logo Patria Investimentos.png",
  },
  {
    name: "Galapagos Capital",
    logo: "/images/Logo_Galapagos_Capital_Color (1).png",
  },
  {
    name: "Itaú",
    logo: "/images/Logo Itau.png",
  },
  {
    name: "Honey Island",
    logo: "/images/Logo Honey Island.png",
  },
  {
    name: "Oliveira Trust",
    logo: "/images/Logo OLIVEIRA TRUST.png",
  },
];

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // 2FA dos usuários internos
  const [challenge, setChallenge] = useState<TwoFactorChallenge | null>(null);
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [pendingSession, setPendingSession] = useState<LoginSession | null>(null);
  const [savedCodes, setSavedCodes] = useState(false);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const finishLogin = (session: LoginSession) => {
    login(session.user, session.accessToken);
    if (session.user.type === "internal") {
      navigate("/admin/dashboard");
    } else {
      navigate("/dashboard");
    }
  };

  const backToPassword = (message?: string) => {
    setChallenge(null);
    setCode("");
    setUseRecoveryCode(false);
    setError(message ?? null);
  };

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(data.email, data.password);
      if (isTwoFactorChallenge(response)) {
        setChallenge(response);
        setCode("");
        setUseRecoveryCode(false);
        return;
      }
      finishLogin(response);
    } catch (err: any) {
      setError(err.message || "Erro ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitCode = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!challenge) return;
    const value = code.trim();
    if (!value) {
      setError(useRecoveryCode ? "Informe o código de recuperação" : "Informe o código de 6 dígitos");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      if (challenge.stage === "setup") {
        const session = await authApi.confirmTwoFactorSetup(challenge.challengeToken, value);
        // mostra os códigos de recuperação antes de entrar
        setChallenge(null);
        setCode("");
        setPendingSession(session);
        return;
      }
      const session = await authApi.verifyTwoFactor(challenge.challengeToken, value);
      finishLogin(session);
    } catch (err: any) {
      const message: string = err.message || "Código inválido";
      // token morto (5 erros, expirado) ou conta bloqueada: volta para a senha
      if (/novamente/i.test(message)) {
        backToPassword(message);
      } else {
        setError(message);
        setCode("");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const copyRecoveryCodes = async () => {
    if (!pendingSession?.recoveryCodes) return;
    try {
      await navigator.clipboard.writeText(pendingSession.recoveryCodes.join("\n"));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const heading = pendingSession
    ? {
        title: "Guarde seus códigos de recuperação",
        subtitle:
          "Cada código entra uma única vez, caso você perca o celular. Eles não serão mostrados de novo.",
      }
    : challenge?.stage === "setup"
      ? {
          title: "Ative a verificação em duas etapas",
          subtitle:
            "Obrigatória para usuários internos. Escaneie o QR code no app autenticador (Google Authenticator, Microsoft Authenticator, 1Password) e digite o código gerado.",
        }
      : challenge
        ? {
            title: "Verificação em duas etapas",
            subtitle: useRecoveryCode
              ? "Digite um dos seus códigos de recuperação."
              : "Digite o código de 6 dígitos do seu app autenticador.",
          }
        : {
            title: "Bem-vindo de volta",
            subtitle: "Entre com suas credenciais para acessar sua conta",
          };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12 lg:py-0 bg-white min-h-screen lg:min-h-0">
        <div className="w-full max-w-md mx-auto">
          {/* Botão Voltar para o Site e Welcome Text */}
          <div className="mb-6 sm:mb-8">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#0038B5] transition-colors mb-4 sm:mb-6"
            >
              <IconArrowLeft className="w-4 h-4" strokeWidth={2} />
              voltar para o site
            </a>
            <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 mb-2">
              {heading.title}
            </h1>
            <p className="text-sm sm:text-base text-neutral-500">{heading.subtitle}</p>
          </div>

          {pendingSession ? (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-2 p-4 rounded-lg border border-neutral-200 bg-neutral-50 font-mono text-sm text-neutral-900">
                {(pendingSession.recoveryCodes ?? []).map((recoveryCode) => (
                  <span key={recoveryCode} className="text-center tracking-wider">
                    {recoveryCode}
                  </span>
                ))}
              </div>
              <Button type="button" variant="outline" className="w-full" onClick={copyRecoveryCodes}>
                {copied ? "Códigos copiados" : "Copiar códigos"}
              </Button>
              <label className="flex items-start gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={savedCodes}
                  onChange={(event) => setSavedCodes(event.target.checked)}
                />
                Guardei os códigos em local seguro
              </label>
              <Button
                type="button"
                className="w-full h-11 text-white hover:opacity-95 focus-visible:ring-[#0038B5]"
                style={{ backgroundColor: "#0038B5" }}
                disabled={!savedCodes}
                onClick={() => finishLogin(pendingSession)}
              >
                Continuar
              </Button>
            </div>
          ) : challenge ? (
            <form onSubmit={onSubmitCode} className="space-y-5">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                  {error}
                </div>
              )}

              {challenge.stage === "setup" && challenge.setup && (
                <div className="flex flex-col items-center gap-3 p-4 rounded-lg border border-neutral-200">
                  <img
                    src={challenge.setup.qrCodeDataUrl}
                    alt="QR code para o app autenticador"
                    className="w-48 h-48"
                  />
                  <p className="text-xs text-neutral-500 text-center">
                    Não consegue escanear? Digite esta chave no app:
                  </p>
                  <code className="text-xs font-mono break-all text-center text-neutral-900 select-all">
                    {challenge.setup.secret}
                  </code>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="two-factor-code" className="text-sm font-medium text-neutral-700">
                  {useRecoveryCode ? "Código de recuperação" : "Código do app autenticador"}
                </Label>
                <Input
                  id="two-factor-code"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  inputMode={useRecoveryCode ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={useRecoveryCode ? 32 : 6}
                  placeholder={useRecoveryCode ? "XXXXX-XXXXX" : "000000"}
                  className="tracking-widest"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 text-white hover:opacity-95 focus-visible:ring-[#0038B5]"
                style={{ backgroundColor: "#0038B5" }}
                loading={isLoading}
              >
                {challenge.stage === "setup" ? "Ativar e entrar" : "Verificar"}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  className="text-neutral-500 hover:text-neutral-900 transition-colors"
                  onClick={() => backToPassword()}
                >
                  Voltar
                </button>
                {challenge.stage === "verify" && (
                  <button
                    type="button"
                    className="text-neutral-500 hover:text-neutral-900 transition-colors"
                    onClick={() => {
                      setUseRecoveryCode(!useRecoveryCode);
                      setCode("");
                      setError(null);
                    }}
                  >
                    {useRecoveryCode ? "Usar o app autenticador" : "Usar código de recuperação"}
                  </button>
                )}
              </div>
            </form>
          ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                {...register("email", {
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido",
                  },
                })}
                error={errors.email?.message}
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-neutral-700">
                  Senha
                </Label>
                <a href="#" className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors">
                  Esqueceu a senha?
                </a>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  {...register("password", {
                    required: "Senha é obrigatória",
                    minLength: {
                      value: 6,
                      message: "Senha deve ter no mínimo 6 caracteres",
                    },
                  })}
                  error={errors.password?.message}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 text-white hover:opacity-95 focus-visible:ring-[#0038B5]" 
              style={{ backgroundColor: '#0038B5' }}
              loading={isLoading}
            >
              Entrar
            </Button>
          </form>
          )}

          {/* Footer */}
          <p className="mt-6 sm:mt-8 text-center text-sm text-neutral-400">
            Não tem uma conta?{" "}
            <a href="/solicitar-acesso" className="text-neutral-900 font-medium hover:underline">
              Solicite acesso
            </a>
          </p>
        </div>
      </div>

      {/* Right Side - Minimalist Panel */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 lg:p-12 relative overflow-hidden"
        style={{
          background: '#0038B5'
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col justify-between min-h-[60vh]">
          {/* Top Section - Logo and Headline */}
          <div className="text-center">
            {/* Logo */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <img
                src="/images/logo-brld.svg"
                alt="BRLD Logo"
                className="h-8 lg:h-10 w-auto brightness-0 invert"
              />
              <span className="text-lg lg:text-xl font-semibold text-white">stableBRLD</span>
            </div>

            {/* Headline */}
            <div className="mb-12">
              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-semibold text-white leading-tight mb-4">
                O real programável<br />
                para sua tesouraria
              </h2>
              <p className="text-sm lg:text-base text-white/80 max-w-md mx-auto">
                Stablecoin institucional pareada 1:1 ao real para liquidação e modernização da tesouraria corporativa.
              </p>
            </div>
          </div>

          {/* Bottom Section - Sócios e Parceiros */}
          <div className="mt-auto">
            <p className="text-xs text-white/70 text-center mb-6 lg:mb-8 font-medium uppercase tracking-wider">
              Sócios e Parceiros
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8">
              {socios.map((socio, index) => {
                // Ajustar tamanho para Patria Investimentos (primeira)
                const isSmallerLogo = socio.name === "Patria Investimentos";
                const isItau = socio.name === "Itaú";
                const isHoneyIsland = socio.name === "Honey Island";
                return (
                  <div
                    key={index}
                    className={`flex items-center justify-center opacity-80 hover:opacity-100 transition-all duration-300 ${
                      isItau || isHoneyIsland ? "h-10 lg:h-12" : isSmallerLogo ? "h-8 lg:h-10" : "h-6 lg:h-8"
                    }`}
                  >
                    <img
                      src={socio.logo}
                      alt={`Logo ${socio.name}`}
                      className={`w-auto object-contain brightness-0 invert ${
                        isItau || isHoneyIsland ? "max-h-10 lg:max-h-12" : isSmallerLogo ? "max-h-8 lg:max-h-10" : "max-h-5 lg:max-h-6"
                      }`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}
